import { useMemo, useState } from 'react';
import { ArrowUpRight, Building2, ExternalLink, Home, Package } from 'lucide-react';
import {
  CONSUMER_DESCRIPTION,
  CONSUMER_KEY_ROWS,
  CONSUMER_LINKS,
  CONSUMER_MAPPING_ROWS,
  CONSUMER_PRODUCTS,
  CONSUMER_TABS,
  CONSUMER_TAGLINE,
  CONSUMER_TAKEAWAYS,
  getConsumerKpis,
  getConsumerStreams,
  type ConsumerProduct,
  type ConsumerStreamId,
  type ConsumerTabId,
} from '@/components/value-chain/starlinkConsumer';
import {
  CoastalBanner,
  ConsumerProductArt,
  MappingProductIcon,
} from '@/components/value-chain/explorer/StarlinkProductArt';
import { CoverageMap } from '@/components/value-chain/explorer/StarlinkProductView';
import { STARLINK_UPDATES } from '@/components/value-chain/starlinkProduct';

type Props = {
  onBackToMap: () => void;
  onBackToStarlink: () => void;
  onOpenValueChain: () => void;
  onOpenSuppliers: () => void;
};

const STREAM_PATH: Record<ConsumerStreamId, string> = {
  consumer: '#2563eb',
  enterprise: '#059669',
  hardware: '#7c3aed',
};

export function StarlinkConsumerView({
  onBackToMap,
  onBackToStarlink,
  onOpenValueChain,
  onOpenSuppliers,
}: Props) {
  const [tab, setTab] = useState<ConsumerTabId>('products');
  const kpis = useMemo(() => getConsumerKpis('q2-2026'), []);
  const streams = useMemo(() => getConsumerStreams('q2-2026'), []);

  const handleTab = (id: ConsumerTabId) => {
    if (id === 'value-chain') {
      onOpenValueChain();
      return;
    }
    if (id === 'suppliers') {
      onOpenSuppliers();
      return;
    }
    setTab(id);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f7f8fa]">
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <nav className="mb-4 flex flex-wrap items-center gap-1 text-[12px] text-slate-400">
          <span>Companies</span>
          <span>›</span>
          <button type="button" onClick={onBackToMap} className="hover:text-slate-700">
            SpaceX
          </button>
          <span>›</span>
          <button type="button" onClick={onBackToStarlink} className="hover:text-slate-700">
            Starlink
          </button>
          <span>›</span>
          <span className="font-medium text-slate-700">Consumer</span>
        </nav>

        <header className="mb-4 flex flex-wrap items-start justify-between gap-6">
          <div className="flex min-w-0 items-start gap-3">
            <StarlinkMark />
            <div className="min-w-0">
              <h1 className="text-[28px] leading-tight font-semibold tracking-tight text-slate-900">
                Starlink – Consumer
              </h1>
              <p className="mt-0.5 text-[13px] font-medium text-slate-500">{CONSUMER_TAGLINE}</p>
              <p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-slate-400">
                {CONSUMER_DESCRIPTION}
              </p>
            </div>
          </div>
          <dl className="flex flex-wrap items-end gap-x-8 gap-y-3">
            {kpis.map((kpi) => (
              <div key={kpi.label} className="text-right">
                <dt className="text-[11px] text-slate-400">{kpi.label}</dt>
                <dd className="mt-1 text-[26px] leading-none font-semibold tracking-tight text-slate-900">
                  {kpi.value}
                </dd>
                {kpi.delta ? (
                  <p
                    className={`mt-1 text-[12px] font-semibold ${
                      kpi.down ? 'text-rose-500' : 'text-emerald-600'
                    }`}
                  >
                    {kpi.down ? '↓' : '↑'} {kpi.delta}
                  </p>
                ) : null}
              </div>
            ))}
          </dl>
        </header>

        <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200">
          {CONSUMER_TABS.map((item) => {
            const selected = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleTab(item.id)}
                className={`relative px-3 py-2.5 text-[13px] ${
                  selected ? 'font-medium text-blue-700' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {item.label}
                {selected ? (
                  <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-blue-600" />
                ) : null}
              </button>
            );
          })}
        </div>

        {tab === 'overview' ? <OverviewTab streams={streams} /> : null}
        {tab === 'products' ? <ProductsTab streams={streams} /> : null}
        {tab === 'geography' ? <GeographyTab /> : null}
        {tab === 'key-data' ? <KeyDataTab /> : null}
        {tab === 'documents' ? <DocumentsTab /> : null}
      </div>
    </div>
  );
}

function OverviewTab({
  streams,
}: {
  streams: ReturnType<typeof getConsumerStreams>;
}) {
  return (
    <div className="space-y-3 pb-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="text-[15px] font-semibold text-slate-900">What this line is</h2>
        <p className="mt-2 max-w-3xl text-[13px] leading-relaxed text-slate-600">
          Connectivity customer-type revenue billed to accounts the 10-Q groups as Consumer: generally
          month-to-month Starlink broadband. The same Standard, Mini, and router hardware also shows up in
          Enterprise & Government service and in Connectivity kit sales.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {streams.map((stream) => (
            <article
              key={stream.id}
              className="rounded-2xl p-4 text-white"
              style={{ background: stream.color }}
            >
              <p className="text-[12px] font-medium opacity-90">{stream.label}</p>
              <p className="mt-1 text-[24px] leading-none font-semibold">{stream.display}</p>
              <p className="mt-2 text-[11px] opacity-85">{stream.detail}</p>
            </article>
          ))}
        </div>
      </section>
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_300px]">
        <ProductGrid compact />
        <TakeawaysCard />
      </div>
    </div>
  );
}

function ProductsTab({
  streams,
}: {
  streams: ReturnType<typeof getConsumerStreams>;
}) {
  return (
    <div className="space-y-3 pb-8">
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-[16px] font-semibold text-slate-900">Consumer Product Ecosystem</h2>
            <p className="mt-0.5 text-[12px] text-slate-500">
              Key Starlink hardware and accessories used by consumer customers (residential, roam, and
              self-serve business).
            </p>
          </div>
          <a
            href="https://www.starlink.com/specifications"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-700 hover:text-blue-900"
          >
            View Product Specs
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
        <ProductGrid />
      </section>

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.85fr)]">
        <RevenueMapping streams={streams} />
        <div className="space-y-3">
          <TakeawaysCard />
          <RelatedLinksCard />
        </div>
      </div>
    </div>
  );
}

function ProductGrid({ compact = false }: { compact?: boolean }) {
  const products = compact ? CONSUMER_PRODUCTS.slice(0, 4) : CONSUMER_PRODUCTS;
  return (
    <div className="grid items-start gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
      {products.map((product) => (
        <ProductCard key={product.id} product={product} />
      ))}
    </div>
  );
}

function ProductCard({ product }: { product: ConsumerProduct }) {
  return (
    <article className="flex min-w-0 flex-col rounded-2xl border border-slate-200 bg-white p-3">
      <div className="flex h-[112px] items-center justify-center rounded-xl bg-slate-50" aria-hidden>
        <ConsumerProductArt kind={product.id} />
      </div>
      <h3 className="mt-3 text-[13px] leading-tight font-semibold text-slate-900">{product.name}</h3>
      <p className="mt-0.5 text-[11px] font-medium text-slate-400">{product.badge}</p>
      <p className="mt-1.5 text-[12px] leading-relaxed text-slate-500">{product.detail}</p>
      <a
        href="https://www.starlink.com/specifications"
        target="_blank"
        rel="noreferrer"
        className="mt-auto pt-2 inline-flex items-center gap-1 text-[12px] font-medium text-blue-700 hover:text-blue-900"
      >
        View details
        <ArrowUpRight className="h-3 w-3" />
      </a>
    </article>
  );
}

function RevenueMapping({
  streams,
}: {
  streams: ReturnType<typeof getConsumerStreams>;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <h2 className="text-[16px] font-semibold text-slate-900">Product to Revenue Stream Mapping</h2>
      <p className="mt-0.5 text-[12px] text-slate-500">
        How Starlink products are used across connectivity revenue streams.
      </p>

      <div className="mt-4 overflow-x-auto">
        <div className="grid min-w-[760px] grid-cols-[108px_minmax(180px,0.9fr)_84px_minmax(260px,1.2fr)] items-stretch gap-0">
          <div className="flex flex-col items-center justify-center rounded-2xl bg-slate-900 px-3 py-6 text-center text-white">
            <StarlinkMark light />
            <p className="mt-3 text-[13px] leading-tight font-semibold">Starlink Products</p>
          </div>

          <ul className="divide-y divide-slate-100 border-y border-slate-100 bg-white">
            {CONSUMER_MAPPING_ROWS.map((row) => (
              <li key={row.id} className="flex h-14 items-center gap-2.5 px-3">
                <MappingProductIcon kind={row.kind} />
                <span>
                  <span className="block text-[13px] font-medium text-slate-800">{row.label}</span>
                  {row.sublabel ? (
                    <span className="block text-[10px] text-slate-400">{row.sublabel}</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>

          <svg viewBox="0 0 84 336" className="h-full w-full min-h-[336px]" aria-hidden>
            {CONSUMER_MAPPING_ROWS.flatMap((row, rowIndex) => {
              const y = 28 + rowIndex * 56;
              return row.streams.map((stream, streamIndex) => {
                const target = streams.findIndex((item) => item.id === stream);
                const ty = 56 + target * 112 + streamIndex * 5;
                return (
                  <path
                    key={`${row.id}-${stream}`}
                    d={`M0 ${y} C34 ${y}, 50 ${ty}, 84 ${ty}`}
                    fill="none"
                    stroke={STREAM_PATH[stream]}
                    strokeWidth="2"
                    opacity="0.88"
                  />
                );
              });
            })}
          </svg>

          <div className="flex flex-col gap-2 py-0 pl-1">
            <p className="text-[11px] font-medium text-slate-400">Connectivity Revenue Streams</p>
            {streams.map((stream) => (
              <article
                key={stream.id}
                className="flex min-h-[92px] flex-1 items-start gap-3 rounded-2xl px-4 py-3 text-white shadow-sm"
                style={{ background: stream.color }}
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15">
                  <StreamIcon id={stream.icon} />
                </span>
                <span>
                  <span className="block text-[13px] font-semibold">{stream.label}</span>
                  <span className="block text-[18px] leading-tight font-semibold">
                    {stream.display} (TTM)
                  </span>
                  <span className="mt-0.5 block text-[11px] text-white/85">{stream.detail}</span>
                </span>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TakeawaysCard() {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="text-[15px] font-semibold text-slate-900">Key Takeaways</h3>
      <ol className="mt-3 space-y-2.5">
        {CONSUMER_TAKEAWAYS.map((insight, index) => (
          <li key={insight} className="flex gap-2.5 text-[12px] leading-relaxed text-slate-600">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-medium text-white">
              {index + 1}
            </span>
            <span>{insight}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function RelatedLinksCard() {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="p-4">
        <h3 className="text-[15px] font-semibold text-slate-900">Related Links</h3>
        <ul className="mt-3 space-y-2">
          {CONSUMER_LINKS.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[12px] text-blue-700 hover:text-blue-900"
              >
                {link.label}
                <ExternalLink className="h-3 w-3" />
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="relative h-[118px]">
        <CoastalBanner />
        <p className="absolute right-3 bottom-3 max-w-[140px] text-right text-[12px] leading-snug font-medium text-white drop-shadow">
          Global internet for a more connected world.
        </p>
      </div>
    </section>
  );
}

function GeographyTab() {
  return (
    <div className="grid gap-3 pb-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-[15px] font-semibold text-slate-900">Consumer coverage</h2>
        <p className="mb-3 text-[12px] leading-relaxed text-slate-500">
          MD&A places Starlink across 167 countries, territories, and other markets. The 10-Q does not
          split Consumer dollars by geography.
        </p>
        <CoverageMap />
      </section>
      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <h2 className="text-[15px] font-semibold text-slate-900">Footprint</h2>
        <dl className="mt-4 space-y-3 text-[13px]">
          <div className="flex justify-between gap-3 border-b border-slate-100 pb-3">
            <dt className="text-slate-500">Markets</dt>
            <dd className="font-semibold text-slate-900">167</dd>
          </div>
          <div className="flex justify-between gap-3 border-b border-slate-100 pb-3">
            <dt className="text-slate-500">Operational satellites (Jun 30)</dt>
            <dd className="font-semibold text-slate-900">10,200+</dd>
          </div>
          <div className="flex justify-between gap-3 border-b border-slate-100 pb-3">
            <dt className="text-slate-500">Subscribers (EoP)</dt>
            <dd className="font-semibold text-slate-900">12.0 million</dd>
          </div>
        </dl>
      </section>
    </div>
  );
}

function KeyDataTab() {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-[15px] font-semibold text-slate-900">Key Data</h2>
      <table className="w-full text-left text-[13px]">
        <thead>
          <tr className="border-b border-slate-200 text-[11px] tracking-wide text-slate-400 uppercase">
            <th className="pb-2 font-medium">Metric</th>
            <th className="pb-2 font-medium">Value</th>
            <th className="pb-2 font-medium">Source</th>
          </tr>
        </thead>
        <tbody>
          {CONSUMER_KEY_ROWS.map((row) => (
            <tr key={row.metric} className="border-b border-slate-100">
              <td className="py-2.5 text-slate-700">{row.metric}</td>
              <td className="py-2.5 font-semibold text-slate-900">{row.value}</td>
              <td className="py-2.5 text-slate-500">{row.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function DocumentsTab() {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-[15px] font-semibold text-slate-900">Documents</h2>
      <ul className="divide-y divide-slate-100">
        {STARLINK_UPDATES.map((item) => (
          <li key={item.title} className="flex items-start justify-between gap-4 py-3">
            <div>
              <p className="text-[13px] font-medium text-slate-900">{item.title}</p>
              <p className="text-[12px] text-slate-500">{item.detail}</p>
            </div>
            <span className="shrink-0 text-[12px] text-slate-400">{item.date}</span>
          </li>
        ))}
        {CONSUMER_LINKS.map((link) => (
          <li key={link.href} className="flex items-start justify-between gap-4 py-3">
            <a
              href={link.href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[13px] font-medium text-blue-700 hover:text-blue-900"
            >
              {link.label}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StreamIcon({ id }: { id: 'home' | 'building' | 'package' }) {
  if (id === 'building') return <Building2 className="h-4 w-4" />;
  if (id === 'package') return <Package className="h-4 w-4" />;
  return <Home className="h-4 w-4" />;
}

function StarlinkMark({ light = false }: { light?: boolean }) {
  return (
    <span
      className={`mt-0.5 flex h-12 w-12 items-center justify-center ${light ? 'text-white' : 'text-slate-900'}`}
      aria-hidden
    >
      <svg viewBox="0 0 64 64" className={light ? 'h-10 w-10' : 'h-11 w-11'}>
        <path d="M6 8h18L58 56H40L6 8Z" fill="currentColor" />
        <path d="M40 8h18L32 36h-12L40 8Z" fill="currentColor" />
      </svg>
    </span>
  );
}
